import { IOConnectBrowser } from "@interopio/browser";
import { IOConnectBrowserPlatform } from "@interopio/browser-platform";

type AppDefinition = IOConnectBrowser.AppManager.Definition | IOConnectBrowser.AppManager.FDC3DefinitionV1 | IOConnectBrowser.AppManager.FDC3DefinitionV2;

const importerAppLimit = 50;

const checkIsAppDefFDC3 = (definition: AppDefinition): definition is IOConnectBrowser.AppManager.FDC3DefinitionV1 | IOConnectBrowser.AppManager.FDC3DefinitionV2 => {
    return !!(definition as IOConnectBrowser.AppManager.FDC3DefinitionV1).appId || !!(definition as IOConnectBrowser.AppManager.FDC3DefinitionV2).appId;
};

const getLocalStorageKey = (userEmail: string) => {
    return `io-sandbox-user-${userEmail}`;
};

const checkImporterAppsLimit = async (io: IOConnectBrowser.API, definitions: AppDefinition[]) => {
    const importedApps = io.appManager.applications().filter((app) => app.userProperties.byAppImporter);

    const totalImporterApps = importedApps.length;

    if ((totalImporterApps + definitions.length) > importerAppLimit) {
        throw new Error(`You can only import ${importerAppLimit} apps. Please remove some apps before importing new ones.`);
    }

    return;
};

const persistAppDefinitions = async (definitions: AppDefinition[], mode: any, userEmail: any) => {
    const localStorageKey = getLocalStorageKey(userEmail);

    const localStorageUserData = localStorage.getItem(localStorageKey);

    const parsedUserData = localStorageUserData ? JSON.parse(localStorageUserData) : {};

    parsedUserData.importedApps = [...(parsedUserData.importedApps || []), ...definitions];

    localStorage.setItem(localStorageKey, JSON.stringify(parsedUserData));
};

const decorateDefinition = (definition: AppDefinition) => {
    if (checkIsAppDefFDC3(definition)) {
        definition.customConfig = {
            ...definition.customConfig,
            byAppImporter: true
        };
        return;
    }

    definition.customProperties = {
        ...definition.customProperties,
        byAppImporter: true
    };
};

const handleAppManagerImport = async (controlMessage: IOConnectBrowserPlatform.ControlMessage, io: IOConnectBrowser.API, platform: IOConnectBrowserPlatform.Plugins.PlatformControls, config: any) => {
    const userEmail = config?.user?.email;

    const callerInstance = io.appManager.instances().find((instance) => instance.id === controlMessage.callerId);

    const appImporterCall = callerInstance?.application.userProperties.persistentAppImporter;

    const definitions = controlMessage.data.definitions as AppDefinition[];
    const mode = controlMessage.data.mode;

    if (appImporterCall && mode === "replace") {
        throw new Error("You cannot replace the apps when importing them via the App Importer. Please use the merge mode.");
    }

    if (appImporterCall && userEmail) {
        await checkImporterAppsLimit(io, definitions);

        definitions.forEach((definition) => decorateDefinition(definition));
    }

    const controlResponse = await platform.system.sendControl({ ...controlMessage, settings: { skipInterception: true } });

    if (appImporterCall && userEmail) {
        persistAppDefinitions(definitions, mode, userEmail);
    }

    return controlResponse;
};

const handleAppManagerRemove = async (controlMessage: IOConnectBrowserPlatform.ControlMessage, io: IOConnectBrowser.API, platform: IOConnectBrowserPlatform.Plugins.PlatformControls, config: any) => {
    const userEmail = config?.user?.email;

    const callerInstance = io.appManager.instances().find((instance) => instance.id === controlMessage.callerId);

    const appImporterCall = callerInstance?.application.userProperties.persistentAppImporter;

    const appNameToDelete = controlMessage.data.name;

    if (appImporterCall) {
        const localStorageKey = getLocalStorageKey(userEmail);

        const localStorageUserData = localStorage.getItem(localStorageKey);

        const parsedUserData = localStorageUserData ? JSON.parse(localStorageUserData) : {};

        const importedApps = parsedUserData.importedApps || [];

        parsedUserData.importedApps = importedApps.filter((app: AppDefinition) => app.name !== appNameToDelete);

        localStorage.setItem(localStorageKey, JSON.stringify(parsedUserData));
    }

    return platform.system.sendControl({ ...controlMessage, settings: { skipInterception: true } });
};

const myInterceptionHandler = async (controlMessage: IOConnectBrowserPlatform.ControlMessage, io: IOConnectBrowser.API, platform: IOConnectBrowserPlatform.Plugins.PlatformControls, config: any) => {

    if (controlMessage.domain === "appManager" && controlMessage.operation === "remove") {
        return handleAppManagerRemove(controlMessage, io, platform, config);
    }

    if (controlMessage.domain === "appManager" && controlMessage.operation === "import") {
        return handleAppManagerImport(controlMessage, io, platform, config);
    }
};

export const start = async (io: IOConnectBrowser.API, config: any, platform: IOConnectBrowserPlatform.Plugins.PlatformControls): Promise<void> => {

    // Defining an interception request.
    const interceptionRequest = {
        callInterceptor: (controlMessage: IOConnectBrowserPlatform.ControlMessage) => {
            return myInterceptionHandler(controlMessage, io, platform, config);
        },
        interceptions: [
            { domain: "appManager" as "appManager", operation: "import" },
            { domain: "appManager" as "appManager", operation: "remove" }
        ]
    };

    // Registering an interception request.
    await platform.interception.register(interceptionRequest);

    console.log("Interception registered");
};
